import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./Roles.scss";

import { useAppSelector, useAppDispatch } from "../../store/index";
import {
  getRolesList,
  getRolesItem,
  RoleItem,
  RolePayload,
} from "../../store/roles/slice";

import DefaultDropdown from "../../components/dropdowns/DefaultDropdown/DefaultDropdown";
import ActionBtn from "../../components/buttons/ActionBtn";
import RolesTable from "./components/RolesTable";
import RoleModal from "./components/RoleModal";

import editIcon from "../../assets/icons/edit-icon.svg";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import { useRefreshList } from "helpers/useRefreshList";
import { RefreshButton } from "components/buttons/RefreshButton";

const RolesList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [selectedRole, setRole] = useState(0);
  const [modalMode, setModalMode] = useState<"create" | "edit" | null>(null);
  const [modalRole, setModalRole] = useState<RoleItem>();
  const [options, setOptions] = useState<
    { value: string | number; label: string }[]
  >([]);

  const rolesList = useAppSelector((store) => store.roles.list);
  const visibleRoles = rolesList.filter(
    (role) => !["player", "admin"].includes(role.title.trim().toLowerCase()),
  );
  const selectedRoleItem = visibleRoles.find(
    (role) => role.id === selectedRole,
  );
  const isListFetched = useAppSelector((store) => store.roles.isListFetched);
  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'roles',
    isListFetched
  });

  useEffect(() => {
    if (selectedRole !== 0) {
      dispatch(getRolesItem(selectedRole));
    }
  }, [selectedRole]);

  useEffect(() => {
    if (!isListFetched) {
      dispatch(getRolesList());
    }
  }, [isListFetched]);

  useEffect(() => {
    if (visibleRoles.length > 0) {
      if (!visibleRoles.some((role) => role.id === selectedRole)) {
        setRole(visibleRoles[0].id);
      }
      const result = visibleRoles.map((el) => {
        return {
          value: el.id,
          label: el.title,
        };
      });
      setOptions(result);
    } else {
      setRole(0);
      setOptions([]);
    }
  }, [rolesList]);

  const isSystemRole =
    selectedRoleItem?.isSystem === true ||
    ["player", "admin"].includes(selectedRoleItem?.title?.toLowerCase() ?? "");

  const handleEdit = async () => {
    if (!selectedRole || isSystemRole) return;
    try {
      const currentRole = await dispatch(getRolesItem(selectedRole)).unwrap();
      if (
        currentRole?.isSystem === true ||
        ["player", "admin"].includes(currentRole?.title?.toLowerCase() ?? "")
      ) {
        return;
      }
      setModalRole(currentRole);
      setModalMode("edit");
    } catch {
      // The existing page remains usable if current role details cannot load.
    }
  };

  const handleSaved = async (role: RoleItem, payload: RolePayload) => {
    const updatedList = await dispatch(getRolesList()).unwrap();
    const matchingRole =
      updatedList.find((item) => item.id === role.id) ??
      updatedList.find(
        (item) => item.title.trim().toLowerCase() === payload.title.toLowerCase(),
      );
    const roleId = matchingRole?.id ?? role.id;
    if (roleId) {
      if (roleId !== selectedRole) {
        setRole(roleId);
      } else {
        await dispatch(getRolesItem(roleId));
      }
    }
  };

  return (
    <div className="roles-list-wrapper">
      <div className="roles-list-header">
        <DefaultDropdown
          onSelect={(e) => {
            setRole(e as number);
          }}
          value={selectedRole}
          options={options}
        />
        <div className="roles-list-actions">
          <ActionBtn
            txt={t("addRole")}
            tooltip="Create Role"
            handleClick={() => {
              setModalRole(undefined);
              setModalMode("create");
            }}
          />
          {!isSystemRole && selectedRoleItem && (
            <ActionBtn
              mainIcon={editIcon}
              txt="Edit Role"
              tooltip="Edit Role"
              handleClick={handleEdit}
            />
          )}
                    <div className="page-list-header--box page-list-header--box--right">
                      <RefreshButton
                        position="right"
                        disabled={isRefreshRequested}
                        isLoading={isRefreshRequested}
                        onClick={() => refresh()}
                      />
                    </div>
        </div>
      </div>
      {!isListFetched ? (
        <DataTableSkeleton columnsCount={8} rowsCount={10} />
      ) : (
        <RolesTable />
      )}
      {modalMode && (
        <RoleModal
          mode={modalMode}
          role={
            modalMode === "edit"
              ? (modalRole ?? selectedRoleItem)
              : undefined
          }
          onClose={() => {
            setModalMode(null);
            setModalRole(undefined);
          }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default RolesList;
